import { useState } from 'react';

import { JxTooltip, JxHint } from '@jx';

import './UmlClassMemberTree.css';

const VISIBILITY_ORDER = ['+', '#', '~', '-'];

// Builds the JxHint comment-block content for a constructor's group-header:
// UML visibility marker + "Constructor Name(params)" header line, then one
// aligned "name : Type" line per dependency (padEnd on the longest name in
// THIS constructor so the colons line up), then any smells below.
function renderCtorHintTitle(member) {
    const dependencies = member.dependencies || [];
    const maxDepNameLen = dependencies.reduce(function widen(max, dep) {
        return Math.max(max, dep.name.length);
    }, 0);
    const hasSmells = member.smells && member.smells.length > 0;
    return (
        <>
            <span className="jx-hint-ctor-comment">
                <span className="jx-hint-ctor-comment-line jx-hint-ctor-comment-header">{`${member.visibility} Constructor ${member.name}(${dependencies
                    .map(function toParamText(d) {
                        return `${d.name}: ${d.type}`;
                    })
                    .join(', ')})`}</span>
                {dependencies.map(function renderDependency(dep, index) {
                    return (
                        <span
                            key={index}
                            className="jx-hint-ctor-comment-line"
                        >{`${member.visibility}      ${dep.name.padEnd(maxDepNameLen)} : ${dep.type}`}</span>
                    );
                })}
            </span>
            {member.description && <span className="jx-hint-description">{member.description}</span>}
            {hasSmells && (
                <span className="jx-hint-smells">
                    {member.smells.map(function renderSmell(smell, index) {
                        return (
                            <span key={index} className="jx-hint-smell-line">
                                {smell}
                            </span>
                        );
                    })}
                </span>
            )}
        </>
    );
}

/**
 * Renders a class's constructors as their own collapsible section, kept
 * OUTSIDE the scrollable UmlClassMemberTree (see UmlClassWidget.jsx) so the
 * dependency overview stays visible without scrolling.
 * @param {{members: {id: string, visibility: string, text: string, memberKind: string}[]}} props
 */
export default function UmlClassConstructorList({ members }) {
    const [collapsedCtors, setCollapsedCtors] = useState(new Set());

    const constructorMembers = members
        .filter(function isCtor(m) {
            return m.memberKind === 'constructor';
        })
        .slice()
        .sort(function byVisibilityThenName(a, b) {
            const va = VISIBILITY_ORDER.indexOf(a.visibility);
            const vb = VISIBILITY_ORDER.indexOf(b.visibility);
            if (va !== vb) return va - vb;
            return a.text.localeCompare(b.text);
        });

    if (constructorMembers.length === 0) {
        return null;
    }

    const toggleCtor = (key, e) => {
        e.stopPropagation();
        setCollapsedCtors(function updateCollapsed(prev) {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <div className="uml-class-member-tree uml-class-constructor-list">
            {constructorMembers.map(function renderCtorNode(member) {
                const isCollapsed = collapsedCtors.has(member.id);
                const hasSmells = member.smells && member.smells.length > 0;
                const dependencies = member.dependencies || [];
                const maxDepNameLen = dependencies.reduce(function widen(max, dep) {
                    return Math.max(max, dep.name.length);
                }, 0);
                return (
                    <div className="uml-class-member-group uml-class-member-group--ctor" key={member.id}>
                        <JxHint title={renderCtorHintTitle(member)}>
                            <div
                                className={`uml-class-member-group-header uml-class-member-group-header--ctor${hasSmells ? ' uml-class-member-group-header--ctor-smell' : ''}`}
                                onMouseDown={(e) => toggleCtor(member.id, e)}
                            >
                                <span className="uml-class-member-group-arrow">{isCollapsed ? '\u25B8' : '\u25BE'}</span>
                                <span className="uml-class-member-group-label uml-class-member-group-label--ctor">{`${member.visibility} ${member.text}`}</span>
                                <span className={`uml-class-member-ctor-badge${hasSmells ? ' uml-class-member-ctor-badge--smell' : ''}`}>
                                    {member.paramCount}
                                </span>
                            </div>
                        </JxHint>
                        {!isCollapsed &&
                            dependencies.map(function renderParamLeaf(dep, index) {
                                return (
                                    <JxTooltip key={index} title={`${dep.name}: ${dep.type}`}>
                                        <div className="uml-class-member-leaf uml-class-member-leaf--ctor-param">
                                            <span className="uml-class-member-leaf-text">{`${dep.name.padEnd(maxDepNameLen)} : ${dep.type}`}</span>
                                        </div>
                                    </JxTooltip>
                                );
                            })}
                    </div>
                );
            })}
        </div>
    );
}
